import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Zap,
  RefreshCw,
  Calendar,
  Bot,
  Code2,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Users,
  Building2,
  Clock,
  Activity,
  PieChart,
  ChevronDown
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';

/**
 * AI Usage & Cost Dashboard
 * Track token usage, costs, and performance metrics
 * Phase 1: Enhanced with agency/user tables and high usage warnings
 */

const AIUsagePage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [summary, setSummary] = useState(null);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [showWarningUsers, setShowWarningUsers] = useState(true);

  // Mock data for Phase 1
  const mockSummary = {
    total_tokens: 2456000,
    total_cost: 73.68,
    assistant_requests: 3420,
    dev_agent_requests: 156,
    avg_tokens_per_request: 718,
    tickets_created: 89,
    tokens_today: 84500,
    cost_today: 2.54
  };

  const mockAgencyUsage = [
    { id: '1', name: 'Premier Credit Repair', tokens: 856000, cost: 25.68, requests: 1245, trend: 'up', warning: false },
    { id: '2', name: 'Credit Solutions Inc', tokens: 623000, cost: 18.69, requests: 890, trend: 'up', warning: true },
    { id: '3', name: 'Fast Track Credit', tokens: 412000, cost: 12.36, requests: 602, trend: 'down', warning: false },
    { id: '4', name: 'Repair My Score', tokens: 298000, cost: 8.94, requests: 421, trend: 'up', warning: false },
    { id: '5', name: 'Credit Heroes LLC', tokens: 267000, cost: 8.01, requests: 262, trend: 'down', warning: true }
  ];

  const mockUserUsage = [
    { id: '1', name: 'John Smith', email: 'john@example.com', agency: 'Premier Credit Repair', tokens: 45200, requests: 67, warning: true },
    { id: '2', name: 'Sarah Johnson', email: 'sarah@example.com', agency: 'Credit Solutions Inc', tokens: 38900, requests: 52, warning: false },
    { id: '3', name: 'Mike Davis', email: 'mike@example.com', agency: 'Fast Track Credit', tokens: 34500, requests: 48, warning: true },
    { id: '4', name: 'Emily Brown', email: 'emily@example.com', agency: 'Premier Credit Repair', tokens: 29800, requests: 41, warning: false },
    { id: '5', name: 'David Wilson', email: 'david@example.com', agency: 'Repair My Score', tokens: 25600, requests: 35, warning: false }
  ];

  const mockModelUsage = {
    small: { tokens: 892000, cost: 0.89, percentage: 36 },
    mid: { tokens: 1124000, cost: 33.72, percentage: 46 },
    strong: { tokens: 440000, cost: 66.00, percentage: 18 }
  };

  useEffect(() => {
    fetchUsageData();
  }, [dateRange]);

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const summaryRes = await fetch('/api/admin/ai/usage/summary', {
        headers: getAuthHeaders()
      });
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      } else {
        setSummary(mockSummary);
      }

      const dailyRes = await fetch(`/api/admin/ai/usage/by-day?days=${dateRange}`, {
        headers: getAuthHeaders()
      });
      if (dailyRes.ok) {
        const data = await dailyRes.json();
        setDailyUsage(data.daily_usage || []);
      }
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
      setSummary(mockSummary);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const StatCard = ({ title, value, subValue, icon: Icon, color, trend }) => (
    <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {subValue && (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                {trend === 'up' && <ArrowUp className="w-3 h-3 text-emerald-400" />}
                {trend === 'down' && <ArrowDown className="w-3 h-3 text-red-400" />}
                {subValue}
              </p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center`} 
               style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const highUsageAgencies = mockAgencyUsage.filter(a => a.warning);
  const highUsageUsers = mockUserUsage.filter(u => u.warning);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            Usage & Cost Dashboard
          </h1>
          <p className="text-slate-400 mt-1">
            Track AI token usage, costs, and system performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="p-2 rounded-lg border border-slate-700 bg-slate-800/50 text-white text-sm"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button
            onClick={fetchUsageData}
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* High Usage Warning Banner */}
      {(highUsageAgencies.length > 0 || highUsageUsers.length > 0) && showWarningUsers && (
        <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-orange-300">High Usage Warning</p>
                <p className="text-sm text-orange-400/80 mt-1">
                  {highUsageAgencies.length} agencies and {highUsageUsers.length} users are approaching their usage limits.
                  Consider reviewing their plans or setting stricter limits.
                </p>
                <div className="flex gap-4 mt-2">
                  {highUsageAgencies.slice(0, 2).map(agency => (
                    <Badge key={agency.id} className="bg-orange-500/20 text-orange-300 border border-orange-500/30">
                      {agency.name}
                    </Badge>
                  ))}
                  {highUsageAgencies.length > 2 && (
                    <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/30">
                      +{highUsageAgencies.length - 2} more
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowWarningUsers(false)}
              className="text-slate-400 hover:text-white"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <Card key={i} className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
              <CardContent className="pt-4">
                <div className="animate-pulse">
                  <div className="h-4 bg-slate-700 rounded w-24 mb-2"></div>
                  <div className="h-8 bg-slate-700 rounded w-16"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Tokens"
            value={formatNumber(summary?.total_tokens || mockSummary.total_tokens)}
            subValue={`${formatNumber(summary?.tokens_today || mockSummary.tokens_today)} today`}
            icon={Zap}
            color="#10b981"
            trend="up"
          />
          <StatCard
            title="Total Cost"
            value={`$${(summary?.total_cost || mockSummary.total_cost).toFixed(2)}`}
            subValue={`$${(summary?.cost_today || mockSummary.cost_today).toFixed(2)} today`}
            icon={DollarSign}
            color="#f59e0b"
          />
          <StatCard
            title="AI Assistant"
            value={formatNumber(summary?.assistant_requests || mockSummary.assistant_requests)}
            subValue="requests"
            icon={Bot}
            color="#3b82f6"
          />
          <StatCard
            title="Dev Agent"
            value={formatNumber(summary?.dev_agent_requests || mockSummary.dev_agent_requests)}
            subValue="tasks"
            icon={Code2}
            color="#8b5cf6"
          />
        </div>
      )}

      {/* Agency Usage Table */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <Building2 className="w-5 h-5 text-blue-400" />
                Agency Usage
              </CardTitle>
              <CardDescription className="text-slate-400">
                Token consumption by agency
              </CardDescription>
            </div>
            <Input
              placeholder="Search agencies..."
              className="w-48 bg-slate-800/50 border-slate-700 text-white text-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Agency</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Tokens</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Cost</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Requests</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Trend</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {mockAgencyUsage.map((agency) => (
                  <tr key={agency.id} className="border-b border-slate-700/30 hover:bg-slate-800/30">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{agency.name}</span>
                        {agency.warning && (
                          <AlertTriangle className="w-4 h-4 text-orange-400" />
                        )}
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-slate-300">{formatNumber(agency.tokens)}</td>
                    <td className="text-right py-3 px-4 text-slate-300">${agency.cost.toFixed(2)}</td>
                    <td className="text-right py-3 px-4 text-slate-300">{agency.requests.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">
                      {agency.trend === 'up' ? (
                        <span className="flex items-center justify-end gap-1 text-emerald-400 text-sm">
                          <ArrowUp className="w-3 h-3" /> Up
                        </span>
                      ) : (
                        <span className="flex items-center justify-end gap-1 text-red-400 text-sm">
                          <ArrowDown className="w-3 h-3" /> Down
                        </span>
                      )}
                    </td>
                    <td className="text-center py-3 px-4">
                      {agency.warning ? (
                        <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30">
                          High Usage
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          Normal
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* User Usage Table */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <Users className="w-5 h-5 text-purple-400" />
                Top Users by Token Usage
              </CardTitle>
              <CardDescription className="text-slate-400">
                Individual user consumption
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Agency</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Tokens</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Requests</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {mockUserUsage.map((user) => (
                  <tr key={user.id} className="border-b border-slate-700/30 hover:bg-slate-800/30">
                    <td className="py-3 px-4">
                      <div>
                        <span className="font-medium text-white">{user.name}</span>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-sm">{user.agency}</td>
                    <td className="text-right py-3 px-4 text-slate-300">{formatNumber(user.tokens)}</td>
                    <td className="text-right py-3 px-4 text-slate-300">{user.requests}</td>
                    <td className="text-center py-3 px-4">
                      {user.warning ? (
                        <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          High
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/30">
                          Normal
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Model Tier Breakdown */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-white">
            <PieChart className="w-5 h-5 text-emerald-400" />
            Model Tier Usage
          </CardTitle>
          <CardDescription className="text-slate-400">
            Token usage breakdown by model tier
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-sm font-medium text-emerald-300">Small Model</span>
                </div>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-0">{mockModelUsage.small.percentage}%</Badge>
              </div>
              <p className="text-2xl font-bold text-emerald-200">{formatNumber(mockModelUsage.small.tokens)}</p>
              <p className="text-xs text-emerald-400">tokens • ${mockModelUsage.small.cost.toFixed(2)} cost</p>
              <p className="text-xs text-emerald-500 mt-2">Classification, search, summaries</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-sm font-medium text-amber-300">Mid Model</span>
                </div>
                <Badge className="bg-amber-500/20 text-amber-300 border-0">{mockModelUsage.mid.percentage}%</Badge>
              </div>
              <p className="text-2xl font-bold text-amber-200">{formatNumber(mockModelUsage.mid.tokens)}</p>
              <p className="text-xs text-amber-400">tokens • ${mockModelUsage.mid.cost.toFixed(2)} cost</p>
              <p className="text-xs text-amber-500 mt-2">UI fixes, mobile, dark mode</p>
            </div>
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-sm font-medium text-purple-300">Strong Model</span>
                </div>
                <Badge className="bg-purple-500/20 text-purple-300 border-0">{mockModelUsage.strong.percentage}%</Badge>
              </div>
              <p className="text-2xl font-bold text-purple-200">{formatNumber(mockModelUsage.strong.tokens)}</p>
              <p className="text-xs text-purple-400">tokens • ${mockModelUsage.strong.cost.toFixed(2)} cost</p>
              <p className="text-xs text-purple-500 mt-2">Backend, auth, billing</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Optimization Tips */}
      <Card className="bg-[rgba(10,38,28,0.65)] border-[rgba(142,218,182,0.12)]">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-white">
            <TrendingUp className="w-5 h-5 text-amber-400" />
            Cost Optimization Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <p className="text-sm font-medium text-slate-300">Use model routing</p>
              <p className="text-xs text-slate-500 mt-1">
                Small models for classification, mid for UI, strong for backend only.
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <p className="text-sm font-medium text-slate-300">Cache common responses</p>
              <p className="text-xs text-slate-500 mt-1">
                Store frequently asked questions and reuse responses.
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <p className="text-sm font-medium text-slate-300">Limit file context</p>
              <p className="text-xs text-slate-500 mt-1">
                Only send relevant code sections, not entire files.
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <p className="text-sm font-medium text-slate-300">Set token caps</p>
              <p className="text-xs text-slate-500 mt-1">
                Configure per-session and daily limits to prevent runaway costs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIUsagePage;
